import * as Sentry from '@sentry/react';

import { isApiError } from '@nucleus-commerce/api-client/transport';

export interface MonitoringOptions {
  dsn: string;
  environment: string;
  release: string;
  surface: 'admin' | 'platform';
}

let enabled = false;

/** Initialises the Sentry SDK against GlitchTip. No-op when dsn is empty (local dev). */
export function initMonitoring({ dsn, environment, release, surface }: MonitoringOptions): void {
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate: 0,
    initialScope: { tags: { surface } },
    beforeSend(event, hint) {
      const error = hint.originalException;
      // Expected API outcomes are not crashes; they are handled by the error policy.
      if (
        isApiError(error) &&
        ['validation', 'unauthorized', 'forbidden', 'not_found', 'cancelled'].includes(error.kind)
      ) {
        return null;
      }
      return event;
    },
  });
  enabled = true;
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (!enabled) return;
  Sentry.withScope((scope) => {
    if (isApiError(error)) {
      scope.setTag('api.kind', error.kind);
      scope.setTag('api.code', error.code);
      if (error.requestId) scope.setTag('api.requestId', error.requestId);
    }
    if (context) scope.setContext('extra', context);
    Sentry.captureException(error);
  });
}

/** Drift worth a look but not a crash, e.g. a tour whose target is gone from its page. */
export function captureWarning(message: string, context?: Record<string, unknown>): void {
  if (!enabled) return;
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    if (context) scope.setContext('extra', context);
    Sentry.captureMessage(message);
  });
}

export function setMonitoringUser(user: { id: string; email?: string } | null): void {
  if (!enabled) return;
  Sentry.setUser(user ? { id: user.id, ...(user.email ? { email: user.email } : {}) } : null);
}

export function setMonitoringTag(key: string, value: string | null): void {
  if (!enabled) return;
  Sentry.setTag(key, value ?? undefined);
}

export const MonitoringErrorBoundary = Sentry.ErrorBoundary;
